import React, { useState, useEffect } from 'react';
import { userService } from '../services/user.service';
import { useAuth } from '../contexts/AuthContext';
import { IUser, UserRole, UserStatus, ISettings, IApiConfig, IApiBaseConfig, IAnthropicConfig, Mode, IModelInfo, IOrganizationInfo } from '../types';

const Users: React.FC = () => {
  const { user: authUser } = useAuth();
  const [users, setUsers] = useState<IUser[]>([]);
  const [selectedUser, setSelectedUser] = useState<IUser | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    firstName: '',
    lastName: '',
    role: UserRole.USER,
    licenseValidUntilDate: ''
  });
  const [settings, setSettings] = useState<ISettings>({ API: [] });
  const [showSettings, setShowSettings] = useState(false);
  const [activeTab, setActiveTab] = useState<'plan' | 'act'>('plan');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [currentOrganization, setCurrentOrganization] = useState<IOrganizationInfo | undefined>(undefined);

  const normalizeSettings = (input?: ISettings, organizationOverride?: IOrganizationInfo): ISettings => ({
    API: input?.API ?? [],
    organization: organizationOverride ?? input?.organization
  });

  const getVisibleUsers = (
    allUsers: IUser[],
    organization?: IOrganizationInfo
  ) => {
    if (!authUser) {
      return allUsers;
    }

    if (authUser.role === UserRole.SUPERADMIN) {
      return allUsers;
    }

    if (authUser.role === UserRole.ADMIN) {
      if (organization?.id) {
        return allUsers.filter(
          (user) => user.settings?.organization?.id === organization.id
        );
      }

      return allUsers.filter(
        (user) => user._id === authUser.id || user.email === authUser.email
      );
    }

    return allUsers;
  };

  useEffect(() => {
    loadUsers();
  }, [authUser?.id]);

  const buildStatusHistoryTooltip = (user: IUser) => {
    if (!user.statusHistory || user.statusHistory.length === 0) {
      return "No status history";
    }

    return user.statusHistory
      .slice()
      .sort(
        (a, b) =>
          new Date(b.changedAt).getTime() - new Date(a.changedAt).getTime()
      )
      .map((entry) => {
        const changedAt = new Date(entry.changedAt).toLocaleString();
        const reason = entry.reason ? ` | Reason: ${entry.reason}` : "";
        const changedBy = entry.changedBy
          ? ` | Changed by: ${entry.changedBy}`
          : "";
        return `Status: ${entry.status} | Changed: ${changedAt}${reason}${changedBy}`;
      })
      .join("\n");
  };

  const loadUsers = async () => {
    try {
      const response = await userService.getAllUsers();
      const allUsers = response.users as IUser[];
      const authUserRecord = authUser
        ? allUsers.find(
            (user) => user._id === authUser.id || user.email === authUser.email
          )
        : undefined;
      const organization = authUserRecord?.settings?.organization;

      setCurrentOrganization(organization);
      setUsers(getVisibleUsers(allUsers, organization));
    } catch (err) {
      console.error('Failed to load users', err);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const normalizedSettings = normalizeSettings(
        settings,
        authUser?.role === UserRole.ADMIN ? currentOrganization : undefined
      );
      if (isEditMode && selectedUser) {
        await userService.updateUser(selectedUser._id, { ...formData, settings: normalizedSettings });
      } else {
        await userService.createUser({ ...formData, settings: normalizedSettings });
      }
      await loadUsers();
      setShowForm(false);
      resetForm();
    } catch (err) {
      console.error(`Failed to ${isEditMode ? 'update' : 'create'} user`, err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEditUser = (user: IUser) => {
    setSelectedUser(user);
    setIsEditMode(true);
    setFormData({
      email: user.email,
      firstName: user.firstName,
      lastName: user.lastName,
      role: user.role,
      licenseValidUntilDate: user.license?.validUntilDate ?
        new Date(user.license.validUntilDate).toISOString().split('T')[0] : ''
    });
    setSettings(
      normalizeSettings(
        user.settings,
        authUser?.role === UserRole.ADMIN ? currentOrganization : undefined
      )
    );
    setShowForm(true);
  };

  const handleAddUser = () => {
    setIsEditMode(false);
    setSelectedUser(null);
    resetForm();
    setShowForm(true);
  };

  const handleRegenerateLicense = async () => {
    if (!selectedUser) return;
    if (confirm('Are you sure you want to regenerate the license?')) {
      try {
        const response = await userService.regenerateLicense(selectedUser._id);
        setSelectedUser(prev => prev ? { ...prev, license: response.user.license } : null);
        await loadUsers();
      } catch (err) {
        console.error('Failed to regenerate license', err);
      }
    }
  };

  const handleToggleMfa = async (user: IUser) => {
    try {
      const updated = await userService.updateUser(user._id, {
        mfaEnabled: !user.mfaEnabled
      });
      const updatedUser = updated.user as IUser;
      setUsers(prev =>
        prev.map((entry) => (entry._id === user._id ? { ...entry, mfaEnabled: updatedUser.mfaEnabled } : entry))
      );
    } catch (err) {
      console.error('Failed to toggle MFA', err);
    }
  };

  const addApiConfig = () => {
    setSettings(prev => ({
      ...prev,
      API: [...prev.API, {
        name: '',
        currentMode: 'plan' as Mode,
        planModeOpenAiModelInfo: {
          openAiBaseUrl: '',
          openAiApiKey: '',
          openAiModelId: ''
        },
        actModeOpenAiModelInfo: {
          openAiBaseUrl: '',
          openAiApiKey: '',
          openAiModelId: ''
        }
      }]
    }));
  };

  const removeApiConfig = (index: number) => {
    setSettings(prev => ({
      ...prev,
      API: prev.API.filter((_, i) => i !== index)
    }));
  };

  const updateApiConfig = (index: number, field: keyof IApiConfig, value: any) => {
    setSettings(prev => ({
      ...prev,
      API: prev.API.map((config, i) => {
        if (i !== index) return config;

        const updated = { ...config, [field]: value };

        // If mode is 'act', sync planModeOpenAiModelInfo to actModeOpenAiModelInfo
        if (updated.currentMode === 'act' && field === 'planModeOpenAiModelInfo') {
          updated.actModeOpenAiModelInfo = value;
        }

        return updated;
      })
    }));
  };

  const updateApiBaseConfig = (index: number, mode: 'plan' | 'act', field: keyof IApiBaseConfig, value: any) => {
    setSettings(prev => ({
      ...prev,
      API: prev.API.map((config, i) => {
        if (i !== index) return config;

        const targetField = mode === 'plan' ? 'planModeOpenAiModelInfo' : 'actModeOpenAiModelInfo';
        const updated = {
          ...config,
          [targetField]: {
            ...config[targetField],
            [field]: value
          }
        };

        // If mode is 'act', sync changes to both
        if (config.currentMode === 'act') {
          const otherField = mode === 'plan' ? 'actModeOpenAiModelInfo' : 'planModeOpenAiModelInfo';
          updated[otherField] = {
            ...updated[targetField]
          } as IApiBaseConfig;
        }

        return updated;
      })
    }));
  };

  const updateModelInfo = (index: number, mode: 'plan' | 'act', field: keyof IModelInfo, value: any) => {
    setSettings(prev => ({
      ...prev,
      API: prev.API.map((config, i) => {
        if (i !== index) return config;

        const targetField = mode === 'plan' ? 'planModeOpenAiModelInfo' : 'actModeOpenAiModelInfo';
        const updated = {
          ...config,
          [targetField]: {
            ...config[targetField],
            modelInfo: {
              ...config[targetField]?.modelInfo,
              [field]: value
            }
          } as IApiBaseConfig
        };

        // If mode is 'act', sync changes to both
        if (config.currentMode === 'act') {
          const otherField = mode === 'plan' ? 'actModeOpenAiModelInfo' : 'planModeOpenAiModelInfo';
          updated[otherField] = {
            ...updated[targetField]
          } as IApiBaseConfig;
        }

        return updated;
      })
    }));
  };

  const updateAnthropicConfig = (index: number, field: keyof IAnthropicConfig, value: string | number | boolean) => {
    setSettings(prev => ({
      ...prev,
      API: prev.API.map((config, i) => {
        if (i !== index) return config;
        return {
          ...config,
          anthropic: {
            ...(config.anthropic ?? { apiKey: '' }),
            [field]: value
          }
        };
      })
    }));
  };

  const updateOrganization = (field: 'id' | 'name', value: string) => {
    setSettings(prev => ({
      ...prev,
      organization: {
        ...(prev.organization ?? { id: '', name: '' }),
        [field]: value
      }
    }));
  };

  const handleDeleteUser = async (userId: string) => {
    if (confirm('Are you sure you want to delete this user?')) {
      try {
        await userService.deleteUser(userId);
        await loadUsers();
      } catch (err) {
        console.error('Failed to delete user', err);
      }
    }
  };

  const handleToggleUserStatus = async (userId: string, currentStatus: UserStatus) => {
    const action = currentStatus === UserStatus.ACTIVE ? 'deactivate' : 'activate';
    const reason = prompt(`Please provide a reason for ${action === 'deactivate' ? 'deactivating' : 'activating'} this user (optional):`);

    if (reason !== null) { // User didn't cancel the prompt
      try {
        await userService.toggleUserStatus(userId, reason || undefined);
        await loadUsers();
      } catch (err) {
        console.error('Failed to toggle user status', err);
      }
    }
  };

  const resetForm = () => {
    setFormData({
      email: '',
      firstName: '',
      lastName: '',
      role: UserRole.USER,
      licenseValidUntilDate: ''
    });
    setSettings(
      normalizeSettings(
        undefined,
        authUser?.role === UserRole.ADMIN ? currentOrganization : undefined
      )
    );
    setShowSettings(false);
    setIsEditMode(false);
    setSelectedUser(null);
  };

  const showOrganizationColumn = authUser?.role === UserRole.SUPERADMIN;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '2rem' }}>
        <h2>User Management</h2>
        <button onClick={handleAddUser} style={buttonStyle}>Add New User</button>
      </div>

      {showForm && (
        <div style={modalStyle} onClick={() => { setShowForm(false); resetForm(); }}>
          <div style={modalContentStyle} onClick={(e) => e.stopPropagation()}>
            <h3>{isEditMode ? 'Edit User' : 'Create New User'}</h3>
            <form onSubmit={handleSubmit} style={formStyle}>
              <div style={inputGroupStyle}>
                <label style={labelStyle}>Email</label>
                <input
                  type="email"
                  placeholder="Enter email address"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  style={inputStyle}
                />
              </div>
              <div style={inputGroupStyle}>
                <label style={labelStyle}>First Name</label>
                <input
                  type="text"
                  placeholder="Enter first name"
                  value={formData.firstName}
                  onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                  required
                  style={inputStyle}
                />
              </div>
              <div style={inputGroupStyle}>
                <label style={labelStyle}>Last Name</label>
                <input
                  type="text"
                  placeholder="Enter last name"
                  value={formData.lastName}
                  onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                  required
                  style={inputStyle}
                />
              </div>
              <div style={inputGroupStyle}>
                <label style={labelStyle}>Role</label>
                <select
                  value={formData.role}
                  onChange={(e) => setFormData({ ...formData, role: e.target.value as UserRole })}
                  style={inputStyle}
                >
                  <option value={UserRole.USER}>User</option>
                  <option value={UserRole.ADMIN}>Admin</option>
                  {authUser?.role === UserRole.SUPERADMIN && (
                    <option value={UserRole.SUPERADMIN}>SuperAdmin</option>
                  )}
                </select>
              </div>
              {authUser?.role === UserRole.SUPERADMIN && (
                <>
                  <div style={inputGroupStyle}>
                    <label style={labelStyle}>Organization ID</label>
                    <input
                      type="text"
                      placeholder="e.g., org-123"
                      value={settings.organization?.id || ''}
                      onChange={(e) => updateOrganization('id', e.target.value)}
                      style={inputStyle}
                    />
                  </div>
                  <div style={inputGroupStyle}>
                    <label style={labelStyle}>Organization Name</label>
                    <input
                      type="text"
                      placeholder="e.g., Acme Inc"
                      value={settings.organization?.name || ''}
                      onChange={(e) => updateOrganization('name', e.target.value)}
                      style={inputStyle}
                    />
                  </div>
                </>
              )}
              {authUser?.role === UserRole.ADMIN && currentOrganization?.id && (
                <div style={inputGroupStyle}>
                  <label style={labelStyle}>Organization</label>
                  <input
                    type="text"
                    value={currentOrganization.name ? `${currentOrganization.name} (${currentOrganization.id})` : currentOrganization.id}
                    readOnly
                    style={{ ...inputStyle, backgroundColor: '#f5f5f5', cursor: 'not-allowed' }}
                  />
                </div>
              )}
              <div style={inputGroupStyle}>
                <label style={labelStyle}>License Valid Until</label>
                <input
                  type="date"
                  placeholder="Select expiration date"
                  value={formData.licenseValidUntilDate}
                  onChange={(e) => setFormData({ ...formData, licenseValidUntilDate: e.target.value })}
                  style={inputStyle}
                />
              </div>

              {isEditMode && selectedUser && (
                <div style={inputGroupStyle}>
                  <label style={labelStyle}>License Key</label>
                  <input
                    type="text"
                    value={selectedUser.license.key}
                    readOnly
                    style={{ ...inputStyle, backgroundColor: '#f5f5f5', cursor: 'not-allowed', width: '100%' }}
                  />
                  <button
                    type="button"
                    onClick={handleRegenerateLicense}
                    style={{ ...buttonStyle, padding: '0.75rem 1rem', marginTop: '0.5rem' }}
                  >
                    Regenerate License Key
                  </button>
                </div>
              )}

              <div style={{ marginTop: '1rem', padding: '1rem', background: '#f9f9f9', borderRadius: '4px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                  <h4 style={{ margin: 0 }}>Settings</h4>
                  <button
                    type="button"
                    onClick={() => setShowSettings(!showSettings)}
                    style={{ ...buttonStyle, padding: '0.5rem 1rem' }}
                  >
                    {showSettings ? 'Hide' : 'Show'} Settings
                  </button>
                </div>

                {showSettings && (
                  <div>
                    {settings?.API?.map((apiConfig, index) => (
                      <div key={index} style={{ marginBottom: '1.5rem', padding: '1rem', background: 'white', borderRadius: '4px', border: '1px solid #ddd' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                          <strong>API Configuration {index + 1}</strong>
                          <button
                            type="button"
                            onClick={() => removeApiConfig(index)}
                            style={{ ...actionButtonStyle, background: '#e74c3c', padding: '0.25rem 0.75rem' }}
                          >
                            Remove
                          </button>
                        </div>

                        <div style={inputGroupStyle}>
                          <label style={labelStyle}>Configuration Name</label>
                          <input
                            type="text"
                            placeholder="e.g., Production, Development, Testing"
                            value={apiConfig.name || ''}
                            onChange={(e) => updateApiConfig(index, 'name', e.target.value)}
                            style={inputStyle}
                          />
                        </div>

                        <div style={inputGroupStyle}>
                          <label style={labelStyle}>Provider Type</label>
                          <select
                            value={apiConfig.providerName || 'OpenAI'}
                            onChange={(e) => updateApiConfig(index, 'providerName', e.target.value)}
                            style={inputStyle}
                          >
                            <option value="OpenAI">OpenAI</option>
                            <option value="Azure">Azure OpenAI</option>
                            <option value="OpenAI Compatible">OpenAI Compatible</option>
                            <option value="Anthropic">Anthropic</option>
                          </select>
                        </div>

                        {apiConfig.providerName === 'Anthropic' && (
                          <div style={{ marginTop: '1rem' }}>
                            <div style={inputGroupStyle}>
                              <label style={labelStyle}>API Key</label>
                              <input
                                type="password"
                                placeholder="sk-ant-..."
                                value={apiConfig.anthropic?.apiKey || ''}
                                onChange={(e) => updateAnthropicConfig(index, 'apiKey', e.target.value)}
                                style={inputStyle}
                              />
                            </div>
                            <div style={inputGroupStyle}>
                              <label style={labelStyle}>Base URL (Optional)</label>
                              <input
                                type="text"
                                placeholder="https://api.anthropic.com"
                                value={apiConfig.anthropic?.anthropicBaseUrl || ''}
                                onChange={(e) => updateAnthropicConfig(index, 'anthropicBaseUrl', e.target.value)}
                                style={inputStyle}
                              />
                            </div>
                            <div style={inputGroupStyle}>
                              <label style={labelStyle}>Plan Mode Model ID</label>
                              <input
                                type="text"
                                placeholder="claude-opus-4-6"
                                value={apiConfig.anthropic?.planModeApiModelId || ''}
                                onChange={(e) => updateAnthropicConfig(index, 'planModeApiModelId', e.target.value)}
                                style={inputStyle}
                              />
                            </div>
                            <div style={inputGroupStyle}>
                              <label style={labelStyle}>Act Mode Model ID</label>
                              <input
                                type="text"
                                placeholder="claude-sonnet-4-6"
                                value={apiConfig.anthropic?.actModeApiModelId || ''}
                                onChange={(e) => updateAnthropicConfig(index, 'actModeApiModelId', e.target.value)}
                                style={inputStyle}
                              />
                            </div>
                            <div style={{ display: 'flex', gap: '1rem' }}>
                              <div style={{ ...inputGroupStyle, flex: 1 }}>
                                <label style={labelStyle}>Max Tokens</label>
                                <input
                                  type="number"
                                  placeholder="8192"
                                  value={apiConfig.anthropic?.maxTokens ?? ''}
                                  onChange={(e) => updateAnthropicConfig(index, 'maxTokens', e.target.value === '' ? 0 : Number(e.target.value))}
                                  style={inputStyle}
                                />
                              </div>
                              <div style={{ ...inputGroupStyle, flex: 1 }}>
                                <label style={labelStyle}>Context Window</label>
                                <input
                                  type="number"
                                  placeholder="200000"
                                  value={apiConfig.anthropic?.contextWindow ?? ''}
                                  onChange={(e) => updateAnthropicConfig(index, 'contextWindow', e.target.value === '' ? 0 : Number(e.target.value))}
                                  style={inputStyle}
                                />
                              </div>
                            </div>
                            <div style={{ display: 'flex', gap: '1rem' }}>
                              <div style={{ ...inputGroupStyle, flex: 1 }}>
                                <label style={labelStyle}>Input Price ($/M tokens)</label>
                                <input
                                  type="number"
                                  step="0.01"
                                  placeholder="3.00"
                                  value={apiConfig.anthropic?.inputPrice ?? ''}
                                  onChange={(e) => updateAnthropicConfig(index, 'inputPrice', e.target.value === '' ? 0 : Number(e.target.value))}
                                  style={inputStyle}
                                />
                              </div>
                              <div style={{ ...inputGroupStyle, flex: 1 }}>
                                <label style={labelStyle}>Output Price ($/M tokens)</label>
                                <input
                                  type="number"
                                  step="0.01"
                                  placeholder="15.00"
                                  value={apiConfig.anthropic?.outputPrice ?? ''}
                                  onChange={(e) => updateAnthropicConfig(index, 'outputPrice', e.target.value === '' ? 0 : Number(e.target.value))}
                                  style={inputStyle}
                                />
                              </div>
                            </div>
                            <div style={{ display: 'flex', gap: '1rem' }}>
                              <div style={{ ...inputGroupStyle, flex: 1 }}>
                                <label style={labelStyle}>Cache Writes Price ($/M tokens)</label>
                                <input
                                  type="number"
                                  step="0.01"
                                  placeholder="3.75"
                                  value={apiConfig.anthropic?.cacheWritesPrice ?? ''}
                                  onChange={(e) => updateAnthropicConfig(index, 'cacheWritesPrice', e.target.value === '' ? 0 : Number(e.target.value))}
                                  style={inputStyle}
                                />
                              </div>
                              <div style={{ ...inputGroupStyle, flex: 1 }}>
                                <label style={labelStyle}>Cache Reads Price ($/M tokens)</label>
                                <input
                                  type="number"
                                  step="0.01"
                                  placeholder="0.30"
                                  value={apiConfig.anthropic?.cacheReadsPrice ?? ''}
                                  onChange={(e) => updateAnthropicConfig(index, 'cacheReadsPrice', e.target.value === '' ? 0 : Number(e.target.value))}
                                  style={inputStyle}
                                />
                              </div>
                            </div>
                            <div style={{ display: 'flex', gap: '2rem', flexWrap: 'wrap', marginTop: '0.5rem' }}>
                              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                                <input
                                  type="checkbox"
                                  checked={apiConfig.anthropic?.supportsImages ?? false}
                                  onChange={(e) => updateAnthropicConfig(index, 'supportsImages', e.target.checked)}
                                />
                                <span style={labelStyle}>Supports Images</span>
                              </label>
                              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                                <input
                                  type="checkbox"
                                  checked={apiConfig.anthropic?.supportsPromptCache ?? false}
                                  onChange={(e) => updateAnthropicConfig(index, 'supportsPromptCache', e.target.checked)}
                                />
                                <span style={labelStyle}>Supports Prompt Cache</span>
                              </label>
                              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                                <input
                                  type="checkbox"
                                  checked={apiConfig.anthropic?.supportsReasoning ?? false}
                                  onChange={(e) => updateAnthropicConfig(index, 'supportsReasoning', e.target.checked)}
                                />
                                <span style={labelStyle}>Supports Reasoning</span>
                              </label>
                            </div>
                          </div>
                        )}

                        {apiConfig.providerName !== 'Anthropic' && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1rem', marginTop: '1rem' }}>
                          <label style={{ ...labelStyle, margin: 0 }}>Sync Plan & Act Settings</label>
                          <label style={toggleSwitchStyle}>
                            <input
                              type="checkbox"
                              checked={apiConfig.currentMode === 'act'}
                              onChange={(e) => {
                                const newMode = e.target.checked ? 'act' : 'plan';
                                updateApiConfig(index, 'currentMode', newMode);
                                if (newMode === 'act' && apiConfig.planModeOpenAiModelInfo) {
                                  // Sync plan to act when enabling
                                  updateApiConfig(index, 'actModeOpenAiModelInfo', apiConfig.planModeOpenAiModelInfo);
                                }
                              }}
                              style={{ display: 'none' }}
                            />
                            <span style={{
                              ...toggleSliderStyle,
                              backgroundColor: apiConfig.currentMode === 'act' ? '#3498db' : '#ccc'
                            }}>
                              <span style={{
                                position: 'absolute',
                                height: '18px',
                                width: '18px',
                                left: apiConfig.currentMode === 'act' ? '26px' : '3px',
                                bottom: '3px',
                                backgroundColor: 'white',
                                transition: '0.4s',
                                borderRadius: '50%'
                              }}></span>
                            </span>
                          </label>
                        </div>
                        )}

                        {apiConfig.providerName !== 'Anthropic' && (apiConfig.currentMode === 'act' ? (
                          <div>
                            <h5 style={{ marginTop: '1rem', marginBottom: '0.5rem' }}>Model Configuration (Synced)</h5>
                            <div style={inputGroupStyle}>
                              <label style={labelStyle}>Base URL</label>
                              <input
                                type="text"
                                placeholder="https://api.openai.com/v1"
                                value={apiConfig.planModeOpenAiModelInfo?.openAiBaseUrl || ''}
                                onChange={(e) => updateApiBaseConfig(index, 'plan', 'openAiBaseUrl', e.target.value)}
                                style={inputStyle}
                              />
                            </div>
                            <div style={inputGroupStyle}>
                              <label style={labelStyle}>API Key</label>
                              <input
                                type="password"
                                placeholder="sk-..."
                                value={apiConfig.planModeOpenAiModelInfo?.openAiApiKey || ''}
                                onChange={(e) => updateApiBaseConfig(index, 'plan', 'openAiApiKey', e.target.value)}
                                style={inputStyle}
                              />
                            </div>
                            <div style={inputGroupStyle}>
                              <label style={labelStyle}>Custom Headers</label>
                              {Object.entries(apiConfig.planModeOpenAiModelInfo?.openAiHeaders || {}).map(([key, value], headerIndex) => (
                                <div key={headerIndex} style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem' }}>
                                  <input
                                    type="text"
                                    placeholder="Header name (e.g., Authorization)"
                                    value={key}
                                    onChange={(e) => {
                                      const headers = { ...(apiConfig.planModeOpenAiModelInfo?.openAiHeaders || {}) };
                                      const newKey = e.target.value;
                                      if (newKey !== key) {
                                        delete headers[key];
                                        if (newKey) headers[newKey] = value;
                                      }
                                      updateApiBaseConfig(index, 'plan', 'openAiHeaders', headers);
                                    }}
                                    style={{ ...inputStyle, flex: 1 }}
                                  />
                                  <input
                                    type="text"
                                    placeholder="Header value (e.g., Bearer token)"
                                    value={value}
                                    onChange={(e) => {
                                      const headers = { ...(apiConfig.planModeOpenAiModelInfo?.openAiHeaders || {}) };
                                      headers[key] = e.target.value;
                                      updateApiBaseConfig(index, 'plan', 'openAiHeaders', headers);
                                    }}
                                    style={{ ...inputStyle, flex: 2 }}
                                  />
                                  <button
                                    type="button"
                                    onClick={() => {
                                      const headers = { ...(apiConfig.planModeOpenAiModelInfo?.openAiHeaders || {}) };
                                      delete headers[key];
                                      updateApiBaseConfig(index, 'plan', 'openAiHeaders', headers);
                                    }}
                                    style={{ ...actionButtonStyle, background: '#e74c3c', padding: '0.75rem' }}
                                  >
                                    ✕
                                  </button>
                                </div>
                              ))}
                              <button
                                type="button"
                                onClick={() => {
                                  const headers = { ...(apiConfig.planModeOpenAiModelInfo?.openAiHeaders || {}) };
                                  headers[''] = '';
                                  updateApiBaseConfig(index, 'plan', 'openAiHeaders', headers);
                                }}
                                style={{ ...buttonStyle, background: '#27ae60', padding: '0.5rem 1rem', fontSize: '0.9rem' }}
                              >
                                + Add Header
                              </button>
                            </div>
                            <div style={inputGroupStyle}>
                              <label style={labelStyle}>Model ID</label>
                              <input
                                type="text"
                                placeholder="gpt-4"
                                value={apiConfig.planModeOpenAiModelInfo?.openAiModelId || ''}
                                onChange={(e) => updateApiBaseConfig(index, 'plan', 'openAiModelId', e.target.value)}
                                style={inputStyle}
                              />
                            </div>
                            <div style={inputGroupStyle}>
                              <label style={labelStyle}>Azure API Version (Optional)</label>
                              <input
                                type="text"
                                placeholder="2023-05-15"
                                value={apiConfig.planModeOpenAiModelInfo?.azureApiVersion || ''}
                                onChange={(e) => updateApiBaseConfig(index, 'plan', 'azureApiVersion', e.target.value)}
                                style={inputStyle}
                              />
                            </div>

                            {apiConfig.providerName === 'OpenAI Compatible' && (
                              <div style={{ marginTop: '1rem', padding: '1rem', background: '#f0f8ff', borderRadius: '4px', border: '1px solid #3498db' }}>
                                <h6 style={{ marginTop: 0, marginBottom: '1rem', color: '#3498db' }}>Extended Model Configuration</h6>

                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                                  <div style={inputGroupStyle}>
                                    <label style={labelStyle}>Max Tokens</label>
                                    <input
                                      type="number"
                                      placeholder="4096"
                                      value={apiConfig.planModeOpenAiModelInfo?.modelInfo?.maxTokens || ''}
                                      onChange={(e) => updateModelInfo(index, 'plan', 'maxTokens', parseInt(e.target.value) || undefined)}
                                      style={inputStyle}
                                    />
                                  </div>
                                  <div style={inputGroupStyle}>
                                    <label style={labelStyle}>Context Window</label>
                                    <input
                                      type="number"
                                      placeholder="128000"
                                      value={apiConfig.planModeOpenAiModelInfo?.modelInfo?.contextWindow || ''}
                                      onChange={(e) => updateModelInfo(index, 'plan', 'contextWindow', parseInt(e.target.value) || undefined)}
                                      style={inputStyle}
                                    />
                                  </div>
                                  <div style={inputGroupStyle}>
                                    <label style={labelStyle}>Input Price (per 1M tokens)</label>
                                    <input
                                      type="number"
                                      step="0.01"
                                      placeholder="2.5"
                                      value={apiConfig.planModeOpenAiModelInfo?.modelInfo?.inputPrice || ''}
                                      onChange={(e) => updateModelInfo(index, 'plan', 'inputPrice', parseFloat(e.target.value) || undefined)}
                                      style={inputStyle}
                                    />
                                  </div>
                                  <div style={inputGroupStyle}>
                                    <label style={labelStyle}>Output Price (per 1M tokens)</label>
                                    <input
                                      type="number"
                                      step="0.01"
                                      placeholder="10.0"
                                      value={apiConfig.planModeOpenAiModelInfo?.modelInfo?.outputPrice || ''}
                                      onChange={(e) => updateModelInfo(index, 'plan', 'outputPrice', parseFloat(e.target.value) || undefined)}
                                      style={inputStyle}
                                    />
                                  </div>
                                  <div style={inputGroupStyle}>
                                    <label style={labelStyle}>Cache Writes Price (per 1M tokens)</label>
                                    <input
                                      type="number"
                                      step="0.01"
                                      placeholder="1.25"
                                      value={apiConfig.planModeOpenAiModelInfo?.modelInfo?.cacheWritesPrice || ''}
                                      onChange={(e) => updateModelInfo(index, 'plan', 'cacheWritesPrice', parseFloat(e.target.value) || undefined)}
                                      style={inputStyle}
                                    />
                                  </div>
                                  <div style={inputGroupStyle}>
                                    <label style={labelStyle}>Cache Reads Price (per 1M tokens)</label>
                                    <input
                                      type="number"
                                      step="0.01"
                                      placeholder="0.1"
                                      value={apiConfig.planModeOpenAiModelInfo?.modelInfo?.cacheReadsPrice || ''}
                                      onChange={(e) => updateModelInfo(index, 'plan', 'cacheReadsPrice', parseFloat(e.target.value) || undefined)}
                                      style={inputStyle}
                                    />
                                  </div>
                                  <div style={inputGroupStyle}>
                                    <label style={labelStyle}>Temperature</label>
                                    <input
                                      type="number"
                                      step="0.1"
                                      min="0"
                                      max="2"
                                      placeholder="0.7"
                                      value={apiConfig.planModeOpenAiModelInfo?.modelInfo?.temperature || ''}
                                      onChange={(e) => updateModelInfo(index, 'plan', 'temperature', parseFloat(e.target.value) || undefined)}
                                      style={inputStyle}
                                    />
                                  </div>
                                  <div style={inputGroupStyle}>
                                    <label style={labelStyle}>System Role</label>
                                    <input
                                      type="text"
                                      placeholder="system"
                                      value={apiConfig.planModeOpenAiModelInfo?.modelInfo?.systemRole || ''}
                                      onChange={(e) => updateModelInfo(index, 'plan', 'systemRole', e.target.value)}
                                      style={inputStyle}
                                    />
                                  </div>
                                </div>

                                <div style={inputGroupStyle}>
                                  <label style={labelStyle}>Description</label>
                                  <input
                                    type="text"
                                    placeholder="GPT-4 Omni - Plan Mode"
                                    value={apiConfig.planModeOpenAiModelInfo?.modelInfo?.description || ''}
                                    onChange={(e) => updateModelInfo(index, 'plan', 'description', e.target.value)}
                                    style={inputStyle}
                                  />
                                </div>

                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1rem', marginTop: '1rem' }}>
                                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                    <input
                                      type="checkbox"
                                      checked={apiConfig.planModeOpenAiModelInfo?.modelInfo?.supportsImages || false}
                                      onChange={(e) => updateModelInfo(index, 'plan', 'supportsImages', e.target.checked)}
                                    />
                                    <span style={{ fontSize: '0.9rem' }}>Supports Images</span>
                                  </label>
                                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                    <input
                                      type="checkbox"
                                      checked={apiConfig.planModeOpenAiModelInfo?.modelInfo?.supportsPromptCache || false}
                                      onChange={(e) => updateModelInfo(index, 'plan', 'supportsPromptCache', e.target.checked)}
                                    />
                                    <span style={{ fontSize: '0.9rem' }}>Supports Prompt Cache</span>
                                  </label>
                                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                    <input
                                      type="checkbox"
                                      checked={apiConfig.planModeOpenAiModelInfo?.modelInfo?.supportsTools || false}
                                      onChange={(e) => updateModelInfo(index, 'plan', 'supportsTools', e.target.checked)}
                                    />
                                    <span style={{ fontSize: '0.9rem' }}>Supports Tools</span>
                                  </label>
                                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                    <input
                                      type="checkbox"
                                      checked={apiConfig.planModeOpenAiModelInfo?.modelInfo?.supportsStreaming || false}
                                      onChange={(e) => updateModelInfo(index, 'plan', 'supportsStreaming', e.target.checked)}
                                    />
                                    <span style={{ fontSize: '0.9rem' }}>Supports Streaming</span>
                                  </label>
                                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                    <input
                                      type="checkbox"
                                      checked={apiConfig.planModeOpenAiModelInfo?.modelInfo?.supportsReasoningEffort || false}
                                      onChange={(e) => updateModelInfo(index, 'plan', 'supportsReasoningEffort', e.target.checked)}
                                    />
                                    <span style={{ fontSize: '0.9rem' }}>Supports Reasoning Effort</span>
                                  </label>
                                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                    <input
                                      type="checkbox"
                                      checked={apiConfig.planModeOpenAiModelInfo?.modelInfo?.isR1FormatRequired || false}
                                      onChange={(e) => updateModelInfo(index, 'plan', 'isR1FormatRequired', e.target.checked)}
                                    />
                                    <span style={{ fontSize: '0.9rem' }}>R1 Format Required</span>
                                  </label>
                                </div>
                              </div>
                            )}
                          </div>
                        ) : (
                          <div>
                            <div style={{ display: 'flex', borderBottom: '2px solid #ddd', marginBottom: '1rem' }}>
                              <button
                                type="button"
                                onClick={() => setActiveTab('plan')}
                                style={{
                                  ...tabButtonStyle,
                                  borderBottom: activeTab === 'plan' ? '2px solid #3498db' : 'none',
                                  color: activeTab === 'plan' ? '#3498db' : '#666'
                                }}
                              >
                                Plan
                              </button>
                              <button
                                type="button"
                                onClick={() => setActiveTab('act')}
                                style={{
                                  ...tabButtonStyle,
                                  borderBottom: activeTab === 'act' ? '2px solid #3498db' : 'none',
                                  color: activeTab === 'act' ? '#3498db' : '#666'
                                }}
                              >
                                Act
                              </button>
                            </div>

                            {activeTab === 'plan' && (
                              <div>
                                <div style={inputGroupStyle}>
                                  <label style={labelStyle}>Base URL</label>
                                  <input
                                    type="text"
                                    placeholder="https://api.openai.com/v1"
                                    value={apiConfig.planModeOpenAiModelInfo?.openAiBaseUrl || ''}
                                    onChange={(e) => updateApiBaseConfig(index, 'plan', 'openAiBaseUrl', e.target.value)}
                                    style={inputStyle}
                                  />
                                </div>
                                <div style={inputGroupStyle}>
                                  <label style={labelStyle}>API Key</label>
                                  <input
                                    type="password"
                                    placeholder="sk-..."
                                    value={apiConfig.planModeOpenAiModelInfo?.openAiApiKey || ''}
                                    onChange={(e) => updateApiBaseConfig(index, 'plan', 'openAiApiKey', e.target.value)}
                                    style={inputStyle}
                                  />
                                </div>
                                <div style={inputGroupStyle}>
                                  <label style={labelStyle}>Custom Headers</label>
                                  {Object.entries(apiConfig.planModeOpenAiModelInfo?.openAiHeaders || {}).map(([key, value], headerIndex) => (
                                    <div key={headerIndex} style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem' }}>
                                      <input
                                        type="text"
                                        placeholder="Header name (e.g., Authorization)"
                                        value={key}
                                        onChange={(e) => {
                                          const headers = { ...(apiConfig.planModeOpenAiModelInfo?.openAiHeaders || {}) };
                                          const newKey = e.target.value;
                                          if (newKey !== key) {
                                            delete headers[key];
                                            if (newKey) headers[newKey] = value;
                                          }
                                          updateApiBaseConfig(index, 'plan', 'openAiHeaders', headers);
                                        }}
                                        style={{ ...inputStyle, flex: 1 }}
                                      />
                                      <input
                                        type="text"
                                        placeholder="Header value (e.g., Bearer token)"
                                        value={value}
                                        onChange={(e) => {
                                          const headers = { ...(apiConfig.planModeOpenAiModelInfo?.openAiHeaders || {}) };
                                          headers[key] = e.target.value;
                                          updateApiBaseConfig(index, 'plan', 'openAiHeaders', headers);
                                        }}
                                        style={{ ...inputStyle, flex: 2 }}
                                      />
                                      <button
                                        type="button"
                                        onClick={() => {
                                          const headers = { ...(apiConfig.planModeOpenAiModelInfo?.openAiHeaders || {}) };
                                          delete headers[key];
                                          updateApiBaseConfig(index, 'plan', 'openAiHeaders', headers);
                                        }}
                                        style={{ ...actionButtonStyle, background: '#e74c3c', padding: '0.75rem' }}
                                      >
                                        ✕
                                      </button>
                                    </div>
                                  ))}
                                  <button
                                    type="button"
                                    onClick={() => {
                                      const headers = { ...(apiConfig.planModeOpenAiModelInfo?.openAiHeaders || {}) };
                                      headers[''] = '';
                                      updateApiBaseConfig(index, 'plan', 'openAiHeaders', headers);
                                    }}
                                    style={{ ...buttonStyle, background: '#27ae60', padding: '0.5rem 1rem', fontSize: '0.9rem' }}
                                  >
                                    + Add Header
                                  </button>
                                </div>
                                <div style={inputGroupStyle}>
                                  <label style={labelStyle}>Model ID</label>
                                  <input
                                    type="text"
                                    placeholder="gpt-4"
                                    value={apiConfig.planModeOpenAiModelInfo?.openAiModelId || ''}
                                    onChange={(e) => updateApiBaseConfig(index, 'plan', 'openAiModelId', e.target.value)}
                                    style={inputStyle}
                                  />
                                </div>
                                <div style={inputGroupStyle}>
                                  <label style={labelStyle}>Azure API Version (Optional)</label>
                                  <input
                                    type="text"
                                    placeholder="2023-05-15"
                                    value={apiConfig.planModeOpenAiModelInfo?.azureApiVersion || ''}
                                    onChange={(e) => updateApiBaseConfig(index, 'plan', 'azureApiVersion', e.target.value)}
                                    style={inputStyle}
                                  />
                                </div>

                                {apiConfig.providerName === 'OpenAI Compatible' && (
                                  <div style={{ marginTop: '1rem', padding: '1rem', background: '#f0f8ff', borderRadius: '4px', border: '1px solid #3498db' }}>
                                    <h6 style={{ marginTop: 0, marginBottom: '1rem', color: '#3498db' }}>Extended Model Configuration</h6>

                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                                      <div style={inputGroupStyle}>
                                        <label style={labelStyle}>Max Tokens</label>
                                        <input
                                          type="number"
                                          placeholder="4096"
                                          value={apiConfig.planModeOpenAiModelInfo?.modelInfo?.maxTokens || ''}
                                          onChange={(e) => updateModelInfo(index, 'plan', 'maxTokens', parseInt(e.target.value) || undefined)}
                                          style={inputStyle}
                                        />
                                      </div>
                                      <div style={inputGroupStyle}>
                                        <label style={labelStyle}>Context Window</label>
                                        <input
                                          type="number"
                                          placeholder="128000"
                                          value={apiConfig.planModeOpenAiModelInfo?.modelInfo?.contextWindow || ''}
                                          onChange={(e) => updateModelInfo(index, 'plan', 'contextWindow', parseInt(e.target.value) || undefined)}
                                          style={inputStyle}
                                        />
                                      </div>
                                      <div style={inputGroupStyle}>
                                        <label style={labelStyle}>Input Price (per 1M tokens)</label>
                                        <input
                                          type="number"
                                          step="0.01"
                                          placeholder="2.5"
                                          value={apiConfig.planModeOpenAiModelInfo?.modelInfo?.inputPrice || ''}
                                          onChange={(e) => updateModelInfo(index, 'plan', 'inputPrice', parseFloat(e.target.value) || undefined)}
                                          style={inputStyle}
                                        />
                                      </div>
                                      <div style={inputGroupStyle}>
                                        <label style={labelStyle}>Output Price (per 1M tokens)</label>
                                        <input
                                          type="number"
                                          step="0.01"
                                          placeholder="10.0"
                                          value={apiConfig.planModeOpenAiModelInfo?.modelInfo?.outputPrice || ''}
                                          onChange={(e) => updateModelInfo(index, 'plan', 'outputPrice', parseFloat(e.target.value) || undefined)}
                                          style={inputStyle}
                                        />
                                      </div>
                                      <div style={inputGroupStyle}>
                                        <label style={labelStyle}>Cache Writes Price (per 1M tokens)</label>
                                        <input
                                          type="number"
                                          step="0.01"
                                          placeholder="1.25"
                                          value={apiConfig.planModeOpenAiModelInfo?.modelInfo?.cacheWritesPrice || ''}
                                          onChange={(e) => updateModelInfo(index, 'plan', 'cacheWritesPrice', parseFloat(e.target.value) || undefined)}
                                          style={inputStyle}
                                        />
                                      </div>
                                      <div style={inputGroupStyle}>
                                        <label style={labelStyle}>Cache Reads Price (per 1M tokens)</label>
                                        <input
                                          type="number"
                                          step="0.01"
                                          placeholder="0.1"
                                          value={apiConfig.planModeOpenAiModelInfo?.modelInfo?.cacheReadsPrice || ''}
                                          onChange={(e) => updateModelInfo(index, 'plan', 'cacheReadsPrice', parseFloat(e.target.value) || undefined)}
                                          style={inputStyle}
                                        />
                                      </div>
                                      <div style={inputGroupStyle}>
                                        <label style={labelStyle}>Temperature</label>
                                        <input
                                          type="number"
                                          step="0.1"
                                          min="0"
                                          max="2"
                                          placeholder="0.7"
                                          value={apiConfig.planModeOpenAiModelInfo?.modelInfo?.temperature || ''}
                                          onChange={(e) => updateModelInfo(index, 'plan', 'temperature', parseFloat(e.target.value) || undefined)}
                                          style={inputStyle}
                                        />
                                      </div>
                                      <div style={inputGroupStyle}>
                                        <label style={labelStyle}>System Role</label>
                                        <input
                                          type="text"
                                          placeholder="system"
                                          value={apiConfig.planModeOpenAiModelInfo?.modelInfo?.systemRole || ''}
                                          onChange={(e) => updateModelInfo(index, 'plan', 'systemRole', e.target.value)}
                                          style={inputStyle}
                                        />
                                      </div>
                                    </div>

                                    <div style={inputGroupStyle}>
                                      <label style={labelStyle}>Description</label>
                                      <input
                                        type="text"
                                        placeholder="GPT-4 Omni - Plan Mode"
                                        value={apiConfig.planModeOpenAiModelInfo?.modelInfo?.description || ''}
                                        onChange={(e) => updateModelInfo(index, 'plan', 'description', e.target.value)}
                                        style={inputStyle}
                                      />
                                    </div>

                                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1rem', marginTop: '1rem' }}>
                                      <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                        <input
                                          type="checkbox"
                                          checked={apiConfig.planModeOpenAiModelInfo?.modelInfo?.supportsImages || false}
                                          onChange={(e) => updateModelInfo(index, 'plan', 'supportsImages', e.target.checked)}
                                        />
                                        <span style={{ fontSize: '0.9rem' }}>Supports Images</span>
                                      </label>
                                      <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                        <input
                                          type="checkbox"
                                          checked={apiConfig.planModeOpenAiModelInfo?.modelInfo?.supportsPromptCache || false}
                                          onChange={(e) => updateModelInfo(index, 'plan', 'supportsPromptCache', e.target.checked)}
                                        />
                                        <span style={{ fontSize: '0.9rem' }}>Supports Prompt Cache</span>
                                      </label>
                                      <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                        <input
                                          type="checkbox"
                                          checked={apiConfig.planModeOpenAiModelInfo?.modelInfo?.supportsTools || false}
                                          onChange={(e) => updateModelInfo(index, 'plan', 'supportsTools', e.target.checked)}
                                        />
                                        <span style={{ fontSize: '0.9rem' }}>Supports Tools</span>
                                      </label>
                                      <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                        <input
                                          type="checkbox"
                                          checked={apiConfig.planModeOpenAiModelInfo?.modelInfo?.supportsStreaming || false}
                                          onChange={(e) => updateModelInfo(index, 'plan', 'supportsStreaming', e.target.checked)}
                                        />
                                        <span style={{ fontSize: '0.9rem' }}>Supports Streaming</span>
                                      </label>
                                      <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                        <input
                                          type="checkbox"
                                          checked={apiConfig.planModeOpenAiModelInfo?.modelInfo?.supportsReasoningEffort || false}
                                          onChange={(e) => updateModelInfo(index, 'plan', 'supportsReasoningEffort', e.target.checked)}
                                        />
                                        <span style={{ fontSize: '0.9rem' }}>Supports Reasoning Effort</span>
                                      </label>
                                      <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                        <input
                                          type="checkbox"
                                          checked={apiConfig.planModeOpenAiModelInfo?.modelInfo?.isR1FormatRequired || false}
                                          onChange={(e) => updateModelInfo(index, 'plan', 'isR1FormatRequired', e.target.checked)}
                                        />
                                        <span style={{ fontSize: '0.9rem' }}>R1 Format Required</span>
                                      </label>
                                    </div>
                                  </div>
                                )}
                              </div>
                            )}

                            {activeTab === 'act' && (
                              <div>
                                <div style={inputGroupStyle}>
                                  <label style={labelStyle}>Base URL</label>
                                  <input
                                    type="text"
                                    placeholder="https://api.openai.com/v1"
                                    value={apiConfig.actModeOpenAiModelInfo?.openAiBaseUrl || ''}
                                    onChange={(e) => updateApiBaseConfig(index, 'act', 'openAiBaseUrl', e.target.value)}
                                    style={inputStyle}
                                  />
                                </div>
                                <div style={inputGroupStyle}>
                                  <label style={labelStyle}>API Key</label>
                                  <input
                                    type="password"
                                    placeholder="sk-..."
                                    value={apiConfig.actModeOpenAiModelInfo?.openAiApiKey || ''}
                                    onChange={(e) => updateApiBaseConfig(index, 'act', 'openAiApiKey', e.target.value)}
                                    style={inputStyle}
                                  />
                                </div>
                                <div style={inputGroupStyle}>
                                  <label style={labelStyle}>Custom Headers</label>
                                  {Object.entries(apiConfig.actModeOpenAiModelInfo?.openAiHeaders || {}).map(([key, value], headerIndex) => (
                                    <div key={headerIndex} style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem' }}>
                                      <input
                                        type="text"
                                        placeholder="Header name (e.g., Authorization)"
                                        value={key}
                                        onChange={(e) => {
                                          const headers = { ...(apiConfig.actModeOpenAiModelInfo?.openAiHeaders || {}) };
                                          const newKey = e.target.value;
                                          if (newKey !== key) {
                                            delete headers[key];
                                            if (newKey) headers[newKey] = value;
                                          }
                                          updateApiBaseConfig(index, 'act', 'openAiHeaders', headers);
                                        }}
                                        style={{ ...inputStyle, flex: 1 }}
                                      />
                                      <input
                                        type="text"
                                        placeholder="Header value (e.g., Bearer token)"
                                        value={value}
                                        onChange={(e) => {
                                          const headers = { ...(apiConfig.actModeOpenAiModelInfo?.openAiHeaders || {}) };
                                          headers[key] = e.target.value;
                                          updateApiBaseConfig(index, 'act', 'openAiHeaders', headers);
                                        }}
                                        style={{ ...inputStyle, flex: 2 }}
                                      />
                                      <button
                                        type="button"
                                        onClick={() => {
                                          const headers = { ...(apiConfig.actModeOpenAiModelInfo?.openAiHeaders || {}) };
                                          delete headers[key];
                                          updateApiBaseConfig(index, 'act', 'openAiHeaders', headers);
                                        }}
                                        style={{ ...actionButtonStyle, background: '#e74c3c', padding: '0.75rem' }}
                                      >
                                        ✕
                                      </button>
                                    </div>
                                  ))}
                                  <button
                                    type="button"
                                    onClick={() => {
                                      const headers = { ...(apiConfig.actModeOpenAiModelInfo?.openAiHeaders || {}) };
                                      headers[''] = '';
                                      updateApiBaseConfig(index, 'act', 'openAiHeaders', headers);
                                    }}
                                    style={{ ...buttonStyle, background: '#27ae60', padding: '0.5rem 1rem', fontSize: '0.9rem' }}
                                  >
                                    + Add Header
                                  </button>
                                </div>
                                <div style={inputGroupStyle}>
                                  <label style={labelStyle}>Model ID</label>
                                  <input
                                    type="text"
                                    placeholder="gpt-4"
                                    value={apiConfig.actModeOpenAiModelInfo?.openAiModelId || ''}
                                    onChange={(e) => updateApiBaseConfig(index, 'act', 'openAiModelId', e.target.value)}
                                    style={inputStyle}
                                  />
                                </div>
                                <div style={inputGroupStyle}>
                                  <label style={labelStyle}>Azure API Version (Optional)</label>
                                  <input
                                    type="text"
                                    placeholder="2023-05-15"
                                    value={apiConfig.actModeOpenAiModelInfo?.azureApiVersion || ''}
                                    onChange={(e) => updateApiBaseConfig(index, 'act', 'azureApiVersion', e.target.value)}
                                    style={inputStyle}
                                  />
                                </div>

                                {apiConfig.providerName === 'OpenAI Compatible' && (
                                  <div style={{ marginTop: '1rem', padding: '1rem', background: '#f0f8ff', borderRadius: '4px', border: '1px solid #3498db' }}>
                                    <h6 style={{ marginTop: 0, marginBottom: '1rem', color: '#3498db' }}>Extended Model Configuration</h6>

                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                                      <div style={inputGroupStyle}>
                                        <label style={labelStyle}>Max Tokens</label>
                                        <input
                                          type="number"
                                          placeholder="4096"
                                          value={apiConfig.actModeOpenAiModelInfo?.modelInfo?.maxTokens || ''}
                                          onChange={(e) => updateModelInfo(index, 'act', 'maxTokens', parseInt(e.target.value) || undefined)}
                                          style={inputStyle}
                                        />
                                      </div>
                                      <div style={inputGroupStyle}>
                                        <label style={labelStyle}>Context Window</label>
                                        <input
                                          type="number"
                                          placeholder="128000"
                                          value={apiConfig.actModeOpenAiModelInfo?.modelInfo?.contextWindow || ''}
                                          onChange={(e) => updateModelInfo(index, 'act', 'contextWindow', parseInt(e.target.value) || undefined)}
                                          style={inputStyle}
                                        />
                                      </div>
                                      <div style={inputGroupStyle}>
                                        <label style={labelStyle}>Input Price (per 1M tokens)</label>
                                        <input
                                          type="number"
                                          step="0.01"
                                          placeholder="2.5"
                                          value={apiConfig.actModeOpenAiModelInfo?.modelInfo?.inputPrice || ''}
                                          onChange={(e) => updateModelInfo(index, 'act', 'inputPrice', parseFloat(e.target.value) || undefined)}
                                          style={inputStyle}
                                        />
                                      </div>
                                      <div style={inputGroupStyle}>
                                        <label style={labelStyle}>Output Price (per 1M tokens)</label>
                                        <input
                                          type="number"
                                          step="0.01"
                                          placeholder="10.0"
                                          value={apiConfig.actModeOpenAiModelInfo?.modelInfo?.outputPrice || ''}
                                          onChange={(e) => updateModelInfo(index, 'act', 'outputPrice', parseFloat(e.target.value) || undefined)}
                                          style={inputStyle}
                                        />
                                      </div>
                                      <div style={inputGroupStyle}>
                                        <label style={labelStyle}>Cache Writes Price (per 1M tokens)</label>
                                        <input
                                          type="number"
                                          step="0.01"
                                          placeholder="1.25"
                                          value={apiConfig.actModeOpenAiModelInfo?.modelInfo?.cacheWritesPrice || ''}
                                          onChange={(e) => updateModelInfo(index, 'act', 'cacheWritesPrice', parseFloat(e.target.value) || undefined)}
                                          style={inputStyle}
                                        />
                                      </div>
                                      <div style={inputGroupStyle}>
                                        <label style={labelStyle}>Cache Reads Price (per 1M tokens)</label>
                                        <input
                                          type="number"
                                          step="0.01"
                                          placeholder="0.1"
                                          value={apiConfig.actModeOpenAiModelInfo?.modelInfo?.cacheReadsPrice || ''}
                                          onChange={(e) => updateModelInfo(index, 'act', 'cacheReadsPrice', parseFloat(e.target.value) || undefined)}
                                          style={inputStyle}
                                        />
                                      </div>
                                      <div style={inputGroupStyle}>
                                        <label style={labelStyle}>Temperature</label>
                                        <input
                                          type="number"
                                          step="0.1"
                                          min="0"
                                          max="2"
                                          placeholder="0.7"
                                          value={apiConfig.actModeOpenAiModelInfo?.modelInfo?.temperature || ''}
                                          onChange={(e) => updateModelInfo(index, 'act', 'temperature', parseFloat(e.target.value) || undefined)}
                                          style={inputStyle}
                                        />
                                      </div>
                                      <div style={inputGroupStyle}>
                                        <label style={labelStyle}>System Role</label>
                                        <input
                                          type="text"
                                          placeholder="system"
                                          value={apiConfig.actModeOpenAiModelInfo?.modelInfo?.systemRole || ''}
                                          onChange={(e) => updateModelInfo(index, 'act', 'systemRole', e.target.value)}
                                          style={inputStyle}
                                        />
                                      </div>
                                    </div>

                                    <div style={inputGroupStyle}>
                                      <label style={labelStyle}>Description</label>
                                      <input
                                        type="text"
                                        placeholder="GPT-4 Omni Mini - Act Mode"
                                        value={apiConfig.actModeOpenAiModelInfo?.modelInfo?.description || ''}
                                        onChange={(e) => updateModelInfo(index, 'act', 'description', e.target.value)}
                                        style={inputStyle}
                                      />
                                    </div>

                                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1rem', marginTop: '1rem' }}>
                                      <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                        <input
                                          type="checkbox"
                                          checked={apiConfig.actModeOpenAiModelInfo?.modelInfo?.supportsImages || false}
                                          onChange={(e) => updateModelInfo(index, 'act', 'supportsImages', e.target.checked)}
                                        />
                                        <span style={{ fontSize: '0.9rem' }}>Supports Images</span>
                                      </label>
                                      <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                        <input
                                          type="checkbox"
                                          checked={apiConfig.actModeOpenAiModelInfo?.modelInfo?.supportsPromptCache || false}
                                          onChange={(e) => updateModelInfo(index, 'act', 'supportsPromptCache', e.target.checked)}
                                        />
                                        <span style={{ fontSize: '0.9rem' }}>Supports Prompt Cache</span>
                                      </label>
                                      <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                        <input
                                          type="checkbox"
                                          checked={apiConfig.actModeOpenAiModelInfo?.modelInfo?.supportsTools || false}
                                          onChange={(e) => updateModelInfo(index, 'act', 'supportsTools', e.target.checked)}
                                        />
                                        <span style={{ fontSize: '0.9rem' }}>Supports Tools</span>
                                      </label>
                                      <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                        <input
                                          type="checkbox"
                                          checked={apiConfig.actModeOpenAiModelInfo?.modelInfo?.supportsStreaming || false}
                                          onChange={(e) => updateModelInfo(index, 'act', 'supportsStreaming', e.target.checked)}
                                        />
                                        <span style={{ fontSize: '0.9rem' }}>Supports Streaming</span>
                                      </label>
                                      <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                        <input
                                          type="checkbox"
                                          checked={apiConfig.actModeOpenAiModelInfo?.modelInfo?.supportsReasoningEffort || false}
                                          onChange={(e) => updateModelInfo(index, 'act', 'supportsReasoningEffort', e.target.checked)}
                                        />
                                        <span style={{ fontSize: '0.9rem' }}>Supports Reasoning Effort</span>
                                      </label>
                                      <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                        <input
                                          type="checkbox"
                                          checked={apiConfig.actModeOpenAiModelInfo?.modelInfo?.isR1FormatRequired || false}
                                          onChange={(e) => updateModelInfo(index, 'act', 'isR1FormatRequired', e.target.checked)}
                                        />
                                        <span style={{ fontSize: '0.9rem' }}>R1 Format Required</span>
                                      </label>
                                    </div>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    ))}

                    <button
                      type="button"
                      onClick={addApiConfig}
                      style={{ ...buttonStyle, width: '100%', background: '#27ae60' }}
                    >
                      + Add API Configuration
                    </button>
                  </div>
                )}
              </div>

              <div style={{ display: 'flex', gap: '1rem', marginTop: '1rem' }}>
                <button type="submit" style={buttonStyle} disabled={isSubmitting}>
                  {isSubmitting
                    ? (isEditMode ? 'Updating...' : 'Creating...')
                    : (isEditMode ? 'Update' : 'Create')}
                </button>
                <button type="button" onClick={() => { setShowForm(false); resetForm(); }} style={{ ...buttonStyle, background: '#95a5a6' }}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

      <table style={tableStyle}>
        <thead>
          <tr>
            <th style={thStyle}>Name</th>
            <th style={thStyle}>Email</th>
            <th style={thStyle}>Role</th>
            {showOrganizationColumn && <th style={thStyle}>Organization</th>}
            <th style={thStyle}>Status</th>
            <th style={thStyle}>MFA</th>
            <th style={thStyle}>License Key</th>
            <th style={thStyle}>Valid Until</th>
            <th style={thStyle}>Created Date</th>
            <th style={thStyle}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {users.map((user) => (
            <tr key={user._id} style={{ backgroundColor: user.status === UserStatus.INACTIVE ? '#fff5f5' : 'white' }}>
              <td style={tdStyle}>{user.firstName} {user.lastName}</td>
              <td style={tdStyle}>{user.email}</td>
              <td style={tdStyle}>{user.role}</td>
              {showOrganizationColumn && (
                <td style={tdStyle}>
                  {user.settings?.organization?.name || user.settings?.organization?.id || 'Unassigned'}
                </td>
              )}
              <td style={tdStyle}>
                <span style={{
                  padding: '0.25rem 0.5rem',
                  borderRadius: '4px',
                  fontSize: '0.85rem',
                  fontWeight: '500',
                  backgroundColor: user.status === UserStatus.ACTIVE ? '#d4edda' : '#f8d7da',
                  color: user.status === UserStatus.ACTIVE ? '#155724' : '#721c24'
                }}
                title={buildStatusHistoryTooltip(user)}
                >
                  {user.status}
                </span>
              </td>
              <td style={tdStyle}>
                <label style={toggleSwitchStyle}>
                  <input
                    type="checkbox"
                    checked={!!user.mfaEnabled}
                    onChange={() => handleToggleMfa(user)}
                    style={{ display: 'none' }}
                  />
                  <span style={{
                    ...toggleSliderStyle,
                    backgroundColor: user.mfaEnabled ? '#3498db' : '#ccc'
                  }}>
                    <span style={{
                      position: 'absolute',
                      height: '18px',
                      width: '18px',
                      left: user.mfaEnabled ? '26px' : '3px',
                      bottom: '3px',
                      backgroundColor: 'white',
                      transition: '0.4s',
                      borderRadius: '50%'
                    }}></span>
                  </span>
                </label>
              </td>
              <td style={tdStyle}><code>{user.license.key}</code></td>
              <td style={tdStyle}>{new Date(user.license.validUntilDate).toLocaleDateString()}</td>
              <td style={tdStyle}>{user.createdAt ? new Date(user.createdAt).toLocaleDateString() : 'N/A'}</td>
              <td style={tdStyle}>
                <button onClick={() => handleEditUser(user)} style={{ ...actionButtonStyle, background: '#f39c12' }}>Edit</button>
                <button
                  onClick={() => handleToggleUserStatus(user._id, user.status)}
                  style={{
                    ...actionButtonStyle,
                    background: user.status === UserStatus.ACTIVE ? '#e67e22' : '#27ae60'
                  }}
                >
                  {user.status === UserStatus.ACTIVE ? 'Deactivate' : 'Activate'}
                </button>
                <button onClick={() => handleDeleteUser(user._id)} style={{ ...actionButtonStyle, background: '#e74c3c' }}>Delete</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const buttonStyle: React.CSSProperties = {
  padding: '0.75rem 1.5rem',
  background: '#3498db',
  color: 'white',
  border: 'none',
  borderRadius: '4px',
  cursor: 'pointer'
};

const actionButtonStyle: React.CSSProperties = {
  padding: '0.5rem 1rem',
  background: '#27ae60',
  color: 'white',
  border: 'none',
  borderRadius: '4px',
  cursor: 'pointer',
  marginRight: '0.5rem',
  fontSize: '0.85rem'
};

const modalStyle: React.CSSProperties = {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  background: 'rgba(0,0,0,0.5)',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  zIndex: 1000,
  overflowY: 'auto',
  padding: '2rem 0'
};

const modalContentStyle: React.CSSProperties = {
  background: 'white',
  padding: '2rem',
  borderRadius: '8px',
  minWidth: '600px',
  maxWidth: '900px',
  maxHeight: '90vh',
  overflowY: 'auto',
  margin: 'auto'
};

const formStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '1rem'
};

const inputGroupStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '0.5rem'
};

const labelStyle: React.CSSProperties = {
  fontWeight: '500',
  fontSize: '0.95rem',
  color: '#2c3e50'
};

const inputStyle: React.CSSProperties = {
  padding: '0.75rem',
  border: '1px solid #ddd',
  borderRadius: '4px',
  fontSize: '1rem'
};

const tableStyle: React.CSSProperties = {
  width: '100%',
  borderCollapse: 'collapse',
  background: 'white',
  boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
};

const thStyle: React.CSSProperties = {
  padding: '1rem',
  textAlign: 'left',
  background: '#2c3e50',
  color: 'white',
  fontWeight: 'bold'
};

const tdStyle: React.CSSProperties = {
  padding: '0.75rem',
  borderBottom: '1px solid #ecf0f1'
};

const toggleSwitchStyle: React.CSSProperties = {
  position: 'relative',
  display: 'inline-block',
  width: '50px',
  height: '24px'
};

const toggleSliderStyle: React.CSSProperties = {
  position: 'absolute',
  cursor: 'pointer',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: '#ccc',
  transition: '0.4s',
  borderRadius: '24px',
  content: '""'
};

const tabButtonStyle: React.CSSProperties = {
  flex: 1,
  padding: '0.75rem 1rem',
  background: 'transparent',
  border: 'none',
  cursor: 'pointer',
  fontSize: '1rem',
  fontWeight: '500',
  transition: 'all 0.3s'
};

export default Users;
